import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  NotificationDetails,
  NotificationFilters,
  NotificationLinkedEntity,
  NotificationListItem,
  NotificationListResult,
  NotificationMarkAllReadResult,
  NotificationMarkReadResult,
  NotificationPreferenceInput,
  NotificationPreferences,
  NotificationUnreadSummary,
} from '../types/notification';
import { calendarDateFromApi } from '../utils/dateTime';
import { getNotificationTypeLabel } from '../utils/notificationType';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function positiveInteger(value: unknown, name: string): number {
  const number = Number(value);
  if (!Number.isInteger(number) || number <= 0) {
    const error = new Error(`${name} must be a positive integer`);
    (error as { code?: string }).code = 'VALIDATION_ERROR';
    throw error;
  }
  return number;
}

function booleanValue(value: unknown, fallback = false): boolean {
  return value === true || value === 1 || value === '1' || value === 'true'
    ? true
    : value === false || value === 0 || value === '0' || value === 'false'
      ? false
      : fallback;
}

function idFromAction(value: unknown, prefix: string): number | null {
  const text = textOrNull(value);
  if (!text) return null;
  const match = new RegExp(`^${prefix}:(\\d+)$`).exec(text);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isInteger(id) && id > 0 ? id : null;
}

function normalizeLinkedEntity(
  value: unknown,
): NotificationLinkedEntity | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as ObjectValue;
  const entityType = textOrNull(
    row.entityType ?? row.linkedEntityType ?? row.referenceType,
  );
  const entityId = numberOrNull(
    row.entityId ?? row.linkedEntityId ?? row.referenceId,
  );
  const workflowInstanceId =
    numberOrNull(row.workflowInstanceId) ??
    idFromAction(row.actionUrl, 'workflow-instance');
  const holidayCalendarId =
    numberOrNull(row.holidayCalendarId) ??
    idFromAction(row.actionUrl, 'holiday-calendar');
  if (
    !entityType &&
    entityId === null &&
    workflowInstanceId === null &&
    holidayCalendarId === null
  ) {
    return null;
  }
  return {
    entityType,
    entityId,
    workflowInstanceId,
    ...(holidayCalendarId ? { holidayCalendarId } : {}),
  };
}

function isNotificationRead(row: ObjectValue): boolean {
  return (
    row.isRead === true ||
    row.isRead === 1 ||
    row.isRead === '1' ||
    Boolean(textOrNull(row.readAt))
  );
}

function normalizeListItem(row: ObjectValue): NotificationListItem | null {
  const notificationId = numberOrNull(row.notificationId);
  if (notificationId === null || notificationId <= 0) return null;

  const notificationTypeCode = textOrNull(
    row.notificationTypeCode ?? row.notificationType,
  );
  const apiLabel = textOrNull(row.notificationTypeLabel);
  const notificationTypeLabel =
    apiLabel &&
    apiLabel.toUpperCase() !== (notificationTypeCode ?? '').toUpperCase()
      ? apiLabel
      : getNotificationTypeLabel(notificationTypeCode);

  return {
    notificationId,
    notificationTypeCode,
    notificationTypeLabel,
    title: textOrNull(row.title ?? row.subject) ?? 'Notification',
    messagePreview: textOrNull(
      row.messagePreview ?? row.messageSummary ?? row.message ?? row.body,
    ),
    createdAt: textOrNull(row.createdAt),
    readAt: textOrNull(row.readAt),
    isRead: isNotificationRead(row),
    priorityCode: textOrNull(row.priorityCode ?? row.priority),
    linkedEntity: normalizeLinkedEntity(row.linkedEntity ?? row),
  };
}

function normalizeDetails(row: ObjectValue): NotificationDetails | null {
  const base = normalizeListItem(row);
  if (!base) return null;
  return {
    ...base,
    message: textOrNull(row.message ?? row.body ?? row.messagePreview),
  };
}

function normalizePreference(row: ObjectValue): NotificationPreferences | null {
  const notificationType = textOrNull(row.notificationType);
  if (!notificationType) return null;
  return {
    userPreferenceId: numberOrNull(row.userPreferenceId),
    notificationType,
    inAppEnabled: booleanValue(row.inAppEnabled),
    pushEnabled: booleanValue(row.pushEnabled),
    emailEnabled: booleanValue(row.emailEnabled),
    smsEnabled: booleanValue(row.smsEnabled),
    quietHoursEnabled: booleanValue(row.quietHoursEnabled),
    quietHoursStart: textOrNull(row.quietHoursStart),
    quietHoursEnd: textOrNull(row.quietHoursEnd),
    preferredLanguageCode: textOrNull(row.preferredLanguageCode),
    isActive: booleanValue(row.isActive, true),
    rowVersion: numberOrNull(row.rowVersion),
  };
}

export async function listNotifications(
  options: NotificationFilters = { page: 1, pageSize: 20 },
): Promise<NotificationListResult> {
  const page = Number(options.page ?? 1);
  const pageSize = Number(options.pageSize ?? 20);
  if (!Number.isInteger(page) || page <= 0) {
    throw Object.assign(new Error('page must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(pageSize) || pageSize <= 0) {
    throw Object.assign(new Error('pageSize must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }

  const params: Record<string, unknown> = { page, pageSize };
  if (options.isRead != null) params.isRead = options.isRead;
  if (options.notificationType?.trim()) {
    params.notificationType = options.notificationType.trim();
  }
  if (options.priority?.trim()) params.priority = options.priority.trim();
  if (options.search?.trim()) params.search = options.search.trim();
  const fromDate = calendarDateFromApi(options.fromDate);
  const toDate = calendarDateFromApi(options.toDate);
  if (fromDate) params.fromDate = fromDate;
  if (toDate) params.toDate = toDate;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
      unreadCount?: unknown;
    }>
  >(API_ENDPOINTS.NOTIFICATIONS.LIST, { params });

  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeListItem)
        .filter((item): item is NotificationListItem => item !== null)
    : [];
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize =
    numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / Math.max(resolvedPageSize, 1)));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
    unreadCount: numberOrNull(data.unreadCount),
  };
}

export async function getNotificationDetails(
  notificationId: number,
): Promise<NotificationDetails> {
  const id = positiveInteger(notificationId, 'notificationId');
  const response = await apiClient.get<
    Envelope<{ notification?: ObjectValue } | ObjectValue>
  >(API_ENDPOINTS.NOTIFICATIONS.DETAILS(id));

  const payload = response.data.data ?? {};
  const row =
    payload &&
    typeof payload === 'object' &&
    'notification' in payload &&
    payload.notification &&
    typeof payload.notification === 'object'
      ? (payload.notification as ObjectValue)
      : (payload as ObjectValue);

  const details = normalizeDetails(row);
  if (!details) {
    throw Object.assign(new Error('Notification was not found.'), {
      code: 'NOTIFICATION_NOT_FOUND',
    });
  }
  return details;
}

export async function getUnreadNotificationCount(): Promise<NotificationUnreadSummary> {
  const response = await apiClient.get<Envelope<{ unreadCount?: unknown }>>(
    API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT,
  );
  const count = numberOrNull(response.data.data?.unreadCount);
  if (count === null || count < 0 || !Number.isInteger(count)) {
    const error = new Error('Unread count is unavailable');
    (error as { code?: string }).code = 'VALIDATION_ERROR';
    throw error;
  }
  return { unreadCount: count };
}

export async function markNotificationAsRead(
  notificationId: number,
): Promise<NotificationMarkReadResult> {
  const id = positiveInteger(notificationId, 'notificationId');
  const response = await apiClient.post<
    Envelope<{
      notificationId?: unknown;
      isRead?: unknown;
      readAt?: unknown;
    }>
  >(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(id));

  const data = response.data.data ?? {};
  return {
    notificationId: numberOrNull(data.notificationId) ?? id,
    isRead: data.isRead === true || data.isRead === 1,
    readAt: textOrNull(data.readAt),
  };
}

export async function markAllNotificationsAsRead(): Promise<NotificationMarkAllReadResult> {
  const response = await apiClient.post<
    Envelope<{ updatedCount?: unknown; unreadCount?: unknown }>
  >(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ);

  const data = response.data.data ?? {};
  const updatedCount = numberOrNull(data.updatedCount);
  const unreadCount = numberOrNull(data.unreadCount);
  return {
    updatedCount:
      updatedCount !== null && updatedCount >= 0 && Number.isInteger(updatedCount)
        ? updatedCount
        : 0,
    unreadCount:
      unreadCount !== null && unreadCount >= 0 && Number.isInteger(unreadCount)
        ? unreadCount
        : 0,
  };
}

export async function getNotificationPreferences(): Promise<
  NotificationPreferences[]
> {
  const response = await apiClient.get<Envelope<{ items?: ObjectValue[] }>>(
    API_ENDPOINTS.NOTIFICATIONS.PREFERENCES,
  );
  const items = Array.isArray(response.data.data?.items)
    ? response.data.data.items
    : [];
  return items
    .map(item => normalizePreference(item))
    .filter((item): item is NotificationPreferences => item !== null);
}

export async function updateNotificationPreferences(
  notificationType: string,
  input: NotificationPreferenceInput,
): Promise<NotificationPreferences> {
  const type = textOrNull(notificationType)?.toUpperCase();
  if (!type || type.length > 30 || !/^[A-Za-z0-9_.-]+$/.test(type)) {
    const error = new Error('notificationType is invalid');
    (error as { code?: string }).code = 'VALIDATION_ERROR';
    throw error;
  }

  const body: NotificationPreferenceInput = {
    inAppEnabled: Boolean(input.inAppEnabled),
    pushEnabled: Boolean(input.pushEnabled),
    emailEnabled: Boolean(input.emailEnabled),
    smsEnabled: Boolean(input.smsEnabled),
    quietHoursEnabled: Boolean(input.quietHoursEnabled),
    quietHoursStart: input.quietHoursEnabled
      ? textOrNull(input.quietHoursStart)
      : null,
    quietHoursEnd: input.quietHoursEnabled
      ? textOrNull(input.quietHoursEnd)
      : null,
    preferredLanguageCode: String(input.preferredLanguageCode || '')
      .trim()
      .toLowerCase(),
  };

  if (!body.preferredLanguageCode) {
    const error = new Error('preferredLanguageCode is required');
    (error as { code?: string }).code = 'VALIDATION_ERROR';
    throw error;
  }

  if (body.quietHoursEnabled && (!body.quietHoursStart || !body.quietHoursEnd)) {
    const error = new Error('Quiet hours require a start and end time');
    (error as { code?: string }).code = 'VALIDATION_ERROR';
    throw error;
  }

  const response = await apiClient.put<
    Envelope<{ preference?: ObjectValue }>
  >(API_ENDPOINTS.NOTIFICATIONS.PREFERENCE(type), body);

  const preference = normalizePreference(response.data.data?.preference ?? {});
  if (!preference) {
    const error = new Error('Preference update returned no data');
    (error as { code?: string }).code = 'VALIDATION_ERROR';
    throw error;
  }
  return preference;
}
